import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { BookOpen, Building2, CalendarDays, Compass, KanbanSquare, LogOut, Settings, ShieldCheck, Sun, ChevronsUpDown, Sparkles, Trash2, Inbox, Users } from "lucide-react";
import { AssistantPanel } from "@/components/common/AssistantPanel";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/today", label: "Today", icon: Sun, id: "today" },
  { to: "/capture", label: "Capture lead", icon: Inbox, id: "capture", perm: "account.create" },
  { to: "/discover", label: "Discover", icon: Compass, id: "discover", perm: "research.run" },
  { to: "/accounts", label: "Accounts", icon: Building2, id: "accounts", perm: "account.view" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, id: "pipeline", perm: "account.view" },
  { to: "/queue", label: "Manager queue", icon: Users, id: "queue", perm: "account.reassign" },
  { to: "/planner", label: "Planner", icon: CalendarDays, id: "planner", perm: "task.manage" },
  { to: "/playbooks", label: "Playbooks", icon: BookOpen, id: "playbooks", perm: "icp.view" },
];

export function AppShell() {
  const { me, can, isPlatformAdmin, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [core, setCore] = useState(null);
  const [ask, setAsk] = useState(false);

  useEffect(() => {
    if (me?.org) api.get("/integrations/core/status").then((r) => setCore(r.data)).catch(() => {});
  }, [me?.org?.id]);

  const switchOrg = async (orgId) => {
    try { await api.post("/me/switch-org", { org_id: orgId }); await refresh(); navigate("/today"); } catch (e) { toast.error(errMsg(e)); }
  };

  const initials = (me?.user?.name || me?.user?.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="flex h-screen bg-background">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card" data-testid="sidebar">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-border">
          <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-sm">V</div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sm">VoroSuite Sales</div>
            <div className="meta truncate max-w-[150px]">{me?.org?.name || "No organisation"}</div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`sidebar-nav-${n.id}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-accent text-accent-foreground" : "text-foreground/80 hover:bg-muted"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          <div className="pt-3 mt-3 border-t border-border space-y-0.5">
            {can("trash.view") && (
              <NavLink to="/trash" data-testid="sidebar-nav-trash" className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-accent text-accent-foreground" : "text-foreground/80 hover:bg-muted"}`}>
                <Trash2 className="h-4 w-4" /> Trash
              </NavLink>
            )}
            <NavLink to="/settings" data-testid="sidebar-nav-settings" className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-accent text-accent-foreground" : "text-foreground/80 hover:bg-muted"}`}>
              <Settings className="h-4 w-4" /> Settings
            </NavLink>
            {isPlatformAdmin && (
              <NavLink to="/platform" data-testid="sidebar-nav-platform" className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-accent text-accent-foreground" : "text-foreground/80 hover:bg-muted"}`}>
                <ShieldCheck className="h-4 w-4" /> Platform admin
              </NavLink>
            )}
          </div>
        </nav>
        <div className="p-3 border-t border-border flex items-center justify-between gap-2">
          <div className="meta truncate">
            {me?.membership ? <>Role: <span className="font-medium text-foreground">{me.membership.role.replace("_", " ")}</span> · scope {me.membership.visibility_scope}</> : "Platform access only"}
          </div>
          <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0" onClick={async () => { await logout(); navigate("/login"); }} data-testid="sidebar-logout" aria-label="Sign out" title="Sign out">
            <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 flex items-center justify-between gap-3 px-4 border-b border-border bg-card" data-testid="topbar">
          <div className="flex items-center gap-2 md:hidden">
            <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-sm">V</div>
            <span className="font-heading font-semibold text-sm">VoroSuite Sales</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
            {me?.memberships?.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="topbar-org-switcher">{me.org?.name} <ChevronsUpDown className="h-3.5 w-3.5 ml-1" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Switch organisation</DropdownMenuLabel>
                  {me.memberships.map((m) => (
                    <DropdownMenuItem key={m.org.id} onClick={() => switchOrg(m.org.id)}>{m.org.name} <span className="meta ml-2">{m.role}</span></DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
          <div className="flex items-center gap-2 min-w-0">
            {me?.org && can("ai.use") && (
              <Button variant="outline" size="sm" className="h-8" onClick={() => setAsk(true)} data-testid="topbar-ask-button"><Sparkles className="h-4 w-4 sm:mr-1.5 text-primary" /><span className="hidden sm:inline">Ask across my data</span></Button>
            )}
            {core && (
              <button onClick={() => navigate("/settings/core")} data-testid="topbar-core-status" className={`chip ${core.connected ? "chip-success" : "chip-muted"} hidden sm:inline-flex`} title="Core by VoroSuite connection">
                <span className={`h-1.5 w-1.5 rounded-full ${core.connected ? "bg-[hsl(var(--success))]" : "bg-muted-foreground"}`} />
                Core {core.connected ? "connected" : "not connected"}{core.pending_handoffs ? ` · ${core.pending_handoffs} queued` : ""}
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-8 w-8 rounded-full bg-accent text-accent-foreground text-xs font-semibold grid place-items-center" data-testid="topbar-user-menu" aria-label="Account menu" title={`${me?.user?.name || "Account"} - account menu`}>{initials}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium">{me?.user?.name}</div>
                  <div className="meta">{me?.user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings/security")}>Security & passkeys</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="topbar-logout"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
        {me?.org && <AssistantPanel open={ask} onOpenChange={setAsk} contextType="workspace" title="Ask across my data" description="Searches your ICPs, accounts, research runs, triage decisions and open opportunities - only what you are allowed to see." suggestions={["Which ICP is producing the best candidates, and where is evidence thin?", "Which accounts should I prioritise this week and why?", "What patterns do my rejected candidates share?", "Which open opportunities have no next action?"]} />}
        <nav className="md:hidden flex border-t border-border bg-card">
          {NAV.filter((n) => !n.perm || can(n.perm)).slice(0, 5).map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `flex-1 flex flex-col items-center py-2 text-[11px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <n.icon className="h-4 w-4" />{n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
